import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

type FeaturePanel = {
  title: string;
  items: readonly string[];
};

type FeaturePanelsSectionProps = {
  width: number;
  panels: readonly FeaturePanel[];
};

const underlineColors = ['primary', 'purple', 'pink'] as const;

export const FeaturePanelsSection = ({
  width,
  panels,
}: FeaturePanelsSectionProps): ReactElement => (
  <Box component="section" sx={{ px: { xs: s('none'), sm: s('none') } }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        rowGap: { xs: s('xl'), md: s('md') },
        columnGap: s('xl'),
        maxWidth: width,
        mx: 'auto',
      }}
    >
      {panels.map((panel, index) => {
        const underlineColor = underlineColors[index % underlineColors.length];
        return (
          <Box
            key={panel.title}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: r('card'),
              paddingY: s('2xl'),
              paddingX: s('xl'),
              paddingTop: s('xl'),
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ mb: s('md') }}>
              <Text
                role="sectionTitle"
                component="h2"
                transform="none"
                sx={(theme) => ({
                  display: 'inline-flex',
                  paddingBottom: s('md'),
                  borderBottom: `${s('xs')} solid ${theme.app.colors[underlineColor]}`,
                })}
              >
                {panel.title}
              </Text>
            </Box>
            <Stack
              component="ul"
              gap="sm"
              sx={{ pl: s('lg'), m: s('none'), color: 'text.secondary' }}
            >
              {panel.items.map((item) => (
                <li key={item}>
                  <Text role="bodyMuted" component="span">
                    {item}
                  </Text>
                </li>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  </Box>
);
